import { useCallback } from 'react';
import { useSetDocumentPositionMutation } from '@frameio/shared/integrations/gql';
import { usePluginFeatures } from '../../../../shared/hooks/usePluginFeatures';
import { useSetSequencePositionMutation } from '../../../gql';

const context = { resolveWithUXP: true } as const;

export type SetSequencePositionMigrationMutationInput = {
  assetId: string;
  commentId: string;
  timestampMicroseconds: number;
  documentId: string | null | undefined;
};

/**
 * Migration bridge: Premiere playhead from a comment — `setDocumentPosition` when
 * {@link usePluginFeatures} enables agnostic marker sync, else legacy `setSequencePosition`.
 * Remove when legacy UXP schema support is dropped.
 */
export function useSetSequencePositionMigrationMutation() {
  const { canUseAgnosticCommentMarkerSync } = usePluginFeatures();
  const [setSequencePositionMutation] = useSetSequencePositionMutation({
    context,
  });
  const [setDocumentPositionMutation] = useSetDocumentPositionMutation({
    context,
  });

  const setPlayheadFromComment = useCallback(
    ({
      assetId,
      commentId,
      documentId,
      timestampMicroseconds,
    }: SetSequencePositionMigrationMutationInput) => {
      if (canUseAgnosticCommentMarkerSync) {
        return setDocumentPositionMutation({
          variables: {
            input: {
              assetId,
              commentId,
              documentId: documentId ?? undefined,
              timestampMicroseconds,
            },
          },
        });
      }

      return setSequencePositionMutation({
        variables: {
          input: {
            assetId,
            commentId,
            timestampMicroseconds,
          },
        },
      });
    },
    [
      setDocumentPositionMutation,
      setSequencePositionMutation,
      canUseAgnosticCommentMarkerSync,
    ]
  );

  return { setPlayheadFromComment };
}
